import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchData, updateData, postData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import Button from '@mui/material/Button';
import CopyrightIcon from '@mui/icons-material/Copyright';
import { CircularProgress, Collapse, IconButton, Select, MenuItem, FormControl } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BusinessIcon from '@mui/icons-material/Business';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { Chip } from '@mui/material';
import BranchCreateModal from './BranchCreateModal';
import BranchEditModal from './BranchEditModal';

const displayValue = (value) => {
    if (value === null || value === undefined || value === '') return 'Not specified';
    return value;
};

const BranchDetailCard = ({ branch }) => (
    <div style={{
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderTop: 'none',
        padding: '16px',
    }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <div style={{ background: 'white', borderRadius: '6px', padding: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', paddingBottom: '6px', borderBottom: '1px solid #e2e8f0' }}>
                    <PhoneIcon style={{ fontSize: '14px', color: '#1e3a5f' }} />
                    <span style={{ fontSize: '11px', fontWeight: '700', color: '#1e3a5f', textTransform: 'uppercase' }}>Contact Info</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12px' }}>
                    <div><span style={{ color: '#64748b', fontSize: '10px', fontWeight: '600' }}>Primary:</span> <span style={{ color: '#1e293b' }}>{displayValue(branch.ContactNumber1)}</span></div>
                    <div><span style={{ color: '#64748b', fontSize: '10px', fontWeight: '600' }}>Secondary:</span> <span style={{ color: '#1e293b' }}>{displayValue(branch.ContactNumber2)}</span></div>
                    <div><span style={{ color: '#64748b', fontSize: '10px', fontWeight: '600' }}>Email:</span> <span style={{ color: '#1e293b' }}>{displayValue(branch.Email)}</span></div>
                </div>
            </div>
            <div style={{ background: 'white', borderRadius: '6px', padding: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', paddingBottom: '6px', borderBottom: '1px solid #e2e8f0' }}>
                    <LocationOnIcon style={{ fontSize: '14px', color: '#1e3a5f' }} />
                    <span style={{ fontSize: '11px', fontWeight: '700', color: '#1e3a5f', textTransform: 'uppercase' }}>Address</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12px' }}>
                    <div><span style={{ color: '#64748b', fontSize: '10px', fontWeight: '600' }}>Line 1:</span> <span style={{ color: '#1e293b' }}>{displayValue(branch.AddressLine1)}</span></div>
                    <div><span style={{ color: '#64748b', fontSize: '10px', fontWeight: '600' }}>Line 2:</span> <span style={{ color: '#1e293b' }}>{displayValue(branch.AddressLine2)}</span></div>
                    <div><span style={{ color: '#64748b', fontSize: '10px', fontWeight: '600' }}>Location:</span> <span style={{ color: '#1e293b' }}>{displayValue(branch.Location)}</span></div>
                    <div><span style={{ color: '#64748b', fontSize: '10px', fontWeight: '600' }}>Pin:</span> <span style={{ color: '#1e293b' }}>{displayValue(branch.PinCode)}</span></div>
                </div>
            </div>
            <div style={{ background: 'white', borderRadius: '6px', padding: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', paddingBottom: '6px', borderBottom: '1px solid #e2e8f0' }}>
                    <ReceiptIcon style={{ fontSize: '14px', color: '#1e3a5f' }} />
                    <span style={{ fontSize: '11px', fontWeight: '700', color: '#1e3a5f', textTransform: 'uppercase' }}>Tax Info</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12px' }}>
                    <div><span style={{ color: '#64748b', fontSize: '10px', fontWeight: '600' }}>VAT Number:</span> <span style={{ color: '#1e293b', fontFamily: 'monospace' }}>{displayValue(branch.VATNumber)}</span></div>
                </div>
            </div>

        </div>
    </div>
);
 
const CompanyEdit = ({ match }) => {
    useSessionValidation();
    const { isAuthenticated, companyId, logout, token, userId } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [openBranchModal, setOpenBranchModal] = useState(false);
    const [openBranchEditModal, setOpenBranchEditModal] = useState(false);
    const [selectedBranchForEdit, setSelectedBranchForEdit] = useState(null);
    const [isSavingBranch, setIsSavingBranch] = useState(false);
    const [expandedBranchId, setExpandedBranchId] = useState(null);
    const [formData, setFormData] = useState([]);
    const [branches, setBranches] = useState([]);

    const handleBranchRowClick = (branchId) => {
        setExpandedBranchId(prevId => prevId === branchId ? null : branchId);
    };

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
    }, [isAuthenticated, companyId]);

    useEffect(() => {
        fetchData('CompanyApi', `/CompanyDetails/${companyId}`, token)
            .then((response) => {
                if (response.responseCode == '1111') {
                    setFormData(response.response_data);
                }
            })
            .catch((error) => console.error('Error fetching Company Details:', error));
    }, []);

    useEffect(() => {
        fetchData('BranchApi', `/${companyId}`, token)
            .then((response) => {
                if (response.responseCode == '1111') {
                    setBranches(response.response_data);
                }
            })
            .catch((error) => console.error('Error fetching Branch Details:', error));
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevState) => ({
            ...prevState,
            [name]: value === 'true' ? true : e.target.type === 'checkbox' ? e.target.checked : value,
        }));
    };

    const handleSubmit = event => {
        event.preventDefault();
        setIsLoading(true);
        setFormData(prevData => ({ ...prevData, UpdatedBy: userId }));
        updateData('CompanyApi', `${companyId}`, formData, token)
            .then((response) => {
                console.log('Record updated successfully:', response.data);
                window.location.reload();
            })
            .catch((error) => {
                console.error('Error updating record:', error);
                setIsLoading(false);
            });
    };

    const handleOpenBranchModal = () => setOpenBranchModal(true);
    const handleCloseBranchModal = () => setOpenBranchModal(false);
    const handleOpenBranchEditModal = (branch) => { setSelectedBranchForEdit(branch); setOpenBranchEditModal(true); };
    const handleCloseBranchEditModal = () => { setOpenBranchEditModal(false); setSelectedBranchForEdit(null); };

    const handleSaveBranch = async (branchData) => {
        setIsSavingBranch(true);
        try {
            const apiData = {
                CompanyId: companyId, Code: branchData.Code, Name: branchData.Name,
                AddressLine1: branchData.AddressLine1, AddressLine2: branchData.AddressLine2,
                ContactNumber1: branchData.ContactNumber1, ContactNumber2: branchData.ContactNumber2,
                ContactNumber3: branchData.ContactNumber3, Email: branchData.Email,
                VATNumber: branchData.VATNumber, PanNumber: branchData.PanNumber,
                ImporterCode: branchData.ImporterCode, TradeLicenseNo: branchData.TradeLicenseNo,
                Location: branchData.Location, PinCode: branchData.PinCode,
                StateCodeId: branchData.StateCodeId ? parseInt(branchData.StateCodeId) : null,
                PaymentBankId: branchData.PaymentBankId ? parseInt(branchData.PaymentBankId) : null,
                BillPrefix: branchData.BillPrefix, 
                //BToCPrefix: branchData.BToCPrefix,
                //IGSTPrefix: branchData.IGSTPrefix,
                //IGSTCustomerPrefix: branchData.IGSTCustomerPrefix,
                CashOpeningBalance: branchData.CashOpeningBalance ? parseFloat(branchData.CashOpeningBalance) : 0,
                CashOpeningDate: branchData.CashOpeningDate || null,
                StockOpeningBalance: branchData.StockOpeningBalance ? parseFloat(branchData.StockOpeningBalance) : 0,
                StockOpeningDate: branchData.StockOpeningDate || null,
                IsEInvoicingEnabled: branchData.IsEInvoicingEnabled, IsEWaybillEnabled: branchData.IsEWaybillEnabled,
                EinvoicePortalUserName: branchData.EinvoicePortalUserName, EinvoicePortalPassword: branchData.EinvoicePortalPassword,
                myGSTcafeCustomerID: branchData.myGSTcafeCustomerID, myGSTcafeApiID: branchData.myGSTcafeApiID,
                myGSTcafeApiSecret: branchData.myGSTcafeApiSecret, CreatedBy: userId,
            };
            const response = await postData('BranchApi', '', apiData, token);
            if (response.responseCode === '1111') {
                setBranches(prevBranches => [...prevBranches, { id: response.response_data.Id || Date.now(), ...apiData }]);
                handleCloseBranchModal();
            }
        } catch (error) { console.error('Error creating branch:', error); }
        finally { setIsSavingBranch(false); }
    };

    const handleUpdateBranch = async (branchData) => {
        setIsSavingBranch(true);
        try {
            const apiData = {
                Id: branchData.Id, CompanyId: companyId, Code: branchData.Code, Name: branchData.Name,
                AddressLine1: branchData.AddressLine1, AddressLine2: branchData.AddressLine2,
                ContactNumber1: branchData.ContactNumber1, ContactNumber2: branchData.ContactNumber2,
                ContactNumber3: branchData.ContactNumber3, Email: branchData.Email,
                VATNumber: branchData.VATNumber, PanNumber: branchData.PanNumber,
                ImporterCode: branchData.ImporterCode, TradeLicenseNo: branchData.TradeLicenseNo,
                Location: branchData.Location, PinCode: branchData.PinCode,
                StateCodeId: branchData.StateCodeId ? parseInt(branchData.StateCodeId) : null,
                PaymentBankId: branchData.PaymentBankId ? parseInt(branchData.PaymentBankId) : null,
                BillPrefix: branchData.BillPrefix,
                // BToCPrefix: selectedBranchForEdit?.BToCPrefix || branchData.BToCPrefix || '',
                // IGSTPrefix: selectedBranchForEdit?.IGSTPrefix || branchData.IGSTPrefix || '',
                // IGSTCustomerPrefix: selectedBranchForEdit?.IGSTCustomerPrefix || branchData.IGSTCustomerPrefix || '',
                CashOpeningBalance: branchData.CashOpeningBalance ? parseFloat(branchData.CashOpeningBalance) : 0,
                CashOpeningDate: branchData.CashOpeningDate || null,
                StockOpeningBalance: branchData.StockOpeningBalance ? parseFloat(branchData.StockOpeningBalance) : 0,
                StockOpeningDate: branchData.StockOpeningDate || null,
                IsEInvoicingEnabled: branchData.IsEInvoicingEnabled, IsEWaybillEnabled: branchData.IsEWaybillEnabled,
                EinvoicePortalUserName: branchData.EinvoicePortalUserName, EinvoicePortalPassword: branchData.EinvoicePortalPassword,
                myGSTcafeCustomerID: branchData.myGSTcafeCustomerID, myGSTcafeApiID: branchData.myGSTcafeApiID,
                myGSTcafeApiSecret: branchData.myGSTcafeApiSecret, ModifiedBy: userId, IsActive: true,
                CreatedBy: branchData.CreatedBy, CreatedDate: branchData.CreatedDate,
            };
            const response = await updateData('BranchApi', branchData.Id, apiData, token);
            if (response.responseCode === '1111') {
                handleCloseBranchEditModal();
                fetchData('BranchApi', `/${companyId}`, token)
                    .then((res) => { if (res.responseCode == '1111') setBranches(res.response_data); })
                    .catch((error) => console.error('Error fetching updated branch data:', error));
            }
        } catch (error) { console.error('Error updating branch:', error); }
        finally { setIsSavingBranch(false); }
    };

    return (
        <div style={{ padding: '16px 20px', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", minHeight: '100vh', background: '#f5f7fa' }}>

            {/* Page Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '800', color: '#1e293b', textTransform: 'uppercase', letterSpacing: '-0.02em' }}>
                    Company
                </h1>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isLoading}
                        startIcon={isLoading ? null : <SaveIcon style={{ fontSize: '16px' }} />}
                        style={{
                            background: '#1e3a5f',
                            color: 'white',
                            padding: '8px 18px',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '600',
                            textTransform: 'none',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                        }}
                    >
                        {isLoading ? <CircularProgress size={16} color="inherit" /> : 'Save'}
                    </Button>
                    <Button
                        variant="outlined"
                        component={Link}
                        to="/Home"
                        startIcon={<ArrowBackIcon style={{ fontSize: '16px' }} />}
                        style={{
                            borderColor: '#1e3a5f',
                            color: '#1e3a5f',
                            padding: '8px 18px',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '600',
                            textTransform: 'none',
                        }}
                    >
                        Dashboard
                    </Button>
                </div>
            </div>

            {/* Two Column Layout */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '16px', alignItems: 'start' }}>

                {/* Left Column - Company Configuration */}
                <div style={{ border: '1px solid #d1d5db', borderRadius: '8px', overflow: 'hidden', background: 'white' }}>
                    <div style={{ background: '#1e3a5f', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <BusinessIcon style={{ fontSize: '16px', color: 'white' }} />
                        <span style={{ fontSize: '12px', fontWeight: '700', color: 'white', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Company Configuration</span>
                    </div>
                    <div style={{ padding: '16px' }}>
                        {/* Company Name */}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>Company Name</label>
                            <input
                                name='Name'
                                value={formData.Name || ''}
                                onChange={handleChange}
                                type="text"
                                placeholder="Enter company name"
                                maxLength={50}
                                style={{
                                    width: '100%', padding: '10px 12px', fontSize: '13px', borderRadius: '6px',
                                    border: '1px solid #d1d5db', outline: 'none', fontWeight: '500', color: '#1e293b',
                                    transition: 'border-color 0.2s',
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                            />
                        </div>

                        {/* Sales Calculation Type & Sales Method */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>Sales Calculation Type</label>
                                <FormControl fullWidth size="small">
                                    <Select
                                        name="SalesCalculationType"
                                        value={formData.SalesCalculationType || 1}
                                        onChange={handleChange}
                                        style={{ fontSize: '13px', borderRadius: '6px', height: '38px', background: 'white' }}
                                    >
                                        <MenuItem value={1} style={{ fontSize: '13px' }}>1. For Inclusive VAT</MenuItem>
                                        <MenuItem value={2} style={{ fontSize: '13px' }}>2. For Exclusive GST</MenuItem>
                                    </Select>
                                </FormControl>
                            </div>                            
                        </div>
                    </div>
                </div>

                {/* Right Column - Branch Network */}
                <div style={{ border: '1px solid #d1d5db', borderRadius: '8px', overflow: 'hidden', background: 'white' }}>
                    <div style={{ background: '#1e3a5f', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <AccountTreeIcon style={{ fontSize: '16px', color: 'white' }} />
                        <span style={{ fontSize: '12px', fontWeight: '700', color: 'white', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Branch Network</span>
                    </div>
                    <div style={{ padding: '12px 14px' }}>
                        {/* Add New Branch Button */}
                        <Button
                            variant="contained"
                            startIcon={<AddIcon style={{ fontSize: '16px' }} />}
                            onClick={handleOpenBranchModal}
                            style={{
                                background: '#10b981',
                                color: 'white',
                                marginBottom: '12px',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '600',
                                padding: '6px 14px',
                                textTransform: 'none',
                                boxShadow: '0 2px 4px rgba(16,185,129,0.3)',
                            }}
                        >
                            Add New Branch
                        </Button>

                        {/* Branch Table */}
                        <div style={{ borderRadius: '6px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                            {/* Table Header */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '30px 50px 70px 1fr 130px 70px 50px',
                                background: '#1e3a5f',
                                padding: '10px 12px',
                                fontSize: '11px',
                                fontWeight: '700',
                                color: 'white',
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                            }}>
                                <div></div>
                                <div>ID</div>
                                <div>Code</div>
                                <div>Name</div>
                                <div>Contact</div>
                                <div>Status</div>
                                <div>Edit</div>
                            </div>

                            {/* Table Rows */}
                            {branches.map((branch, index) => (
                                <div key={branch.Id || index}>
                                    <div
                                        onClick={() => handleBranchRowClick(branch.Id)}
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '30px 50px 70px 1fr 130px 70px 50px',
                                            padding: '10px 12px',
                                            fontSize: '12px',
                                            fontWeight: '500',
                                            color: '#1e293b',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            borderBottom: '1px solid #f1f5f9',
                                            background: index % 2 === 0 ? 'white' : '#f8fafc',
                                            transition: 'background 0.15s',
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#eff6ff'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = index % 2 === 0 ? 'white' : '#f8fafc'}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {expandedBranchId === branch.Id
                                                ? <ExpandLessIcon style={{ fontSize: '18px', color: '#1e3a5f' }} />
                                                : <ExpandMoreIcon style={{ fontSize: '18px', color: '#6b7280' }} />
                                            }
                                        </div>
                                        <div style={{ fontWeight: '600' }}>{branch.Id}</div>
                                        <div style={{ fontFamily: 'monospace', fontWeight: '600', color: '#1e3a5f', background: '#e0e7ff', padding: '2px 6px', borderRadius: '4px', fontSize: '11px', textAlign: 'center' }}>{branch.Code}</div>
                                        <div style={{ fontWeight: '600' }}>{branch.Name}</div>
                                        <div style={{ fontFamily: 'monospace', fontSize: '12px', color: '#475569' }}>{branch.ContactNumber1}</div>
                                        <div>
                                            <span style={{
                                                fontSize: '10px', fontWeight: '600', padding: '3px 8px', borderRadius: '12px',
                                                background: branch.IsActive ? '#dcfce7' : '#fee2e2',
                                                color: branch.IsActive ? '#166534' : '#991b1b',
                                            }}>
                                                {branch.IsActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => { e.stopPropagation(); handleOpenBranchEditModal(branch); }}
                                                style={{ color: '#1e3a5f', padding: '4px', background: '#e0e7ff', borderRadius: '4px' }}
                                            >
                                                <EditIcon style={{ fontSize: '14px' }} />
                                            </IconButton>
                                        </div>
                                    </div>
                                    <Collapse in={expandedBranchId === branch.Id} timeout={250}>
                                        <BranchDetailCard branch={branch} />
                                    </Collapse>
                                </div>
                            ))}

                            {branches.length === 0 && (
                                <div style={{ padding: '24px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                                    No branches found. Add a new branch to get started.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div style={{ textAlign: 'center', padding: '20px 0 8px', fontSize: '12px', color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                <CopyrightIcon style={{ fontSize: '14px', color: '#9ca3af' }} />
                <span>EFFTABS SOLUTIONS LLP</span>
            </div>

            {/* Modals */}
            <BranchCreateModal open={openBranchModal} onClose={handleCloseBranchModal} onSave={handleSaveBranch} isSaving={isSavingBranch} />
            <BranchEditModal open={openBranchEditModal} onClose={handleCloseBranchEditModal} onSave={handleUpdateBranch} isSaving={isSavingBranch} branchData={selectedBranchForEdit} />

            <style>{`
                @media (max-width: 768px) {
                    div[style*="grid-template-columns: 1fr 1.5fr"] {
                        grid-template-columns: 1fr !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default CompanyEdit;
